import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { api } from '@/lib/api-client';
import { Server, Circle, AlertTriangle } from 'lucide-react';
import { capabilityColors } from '@/lib/capability-colors';
import { HEADER_HOSTING_HEIGHT_CLASSES } from './worker-hosting-header-dimensions';

interface HostingWorker {
    name: string;
    status: string;
    modified: string;
    app_key?: string;
    ext?: {
        host?: string;
        pid?: number;
        [key: string]: unknown;
    };
    config?: {
        capabilities_hosted?: Array<{ name: string; type: string }>;
        [key: string]: unknown;
    };
}

const statusDotColors: Record<string, string> = {
    online: 'text-emerald-500',
    draining: 'text-amber-500',
    offline: 'text-slate-400',
};

const statusBgColors: Record<string, string> = {
    online: 'bg-emerald-50 text-emerald-700',
    draining: 'bg-amber-50 text-amber-700',
    offline: 'bg-slate-50 text-slate-500',
};

interface WorkerHostingInfoProps {
    capabilityName: string;
    capabilityType: string;
    appKey?: string;
    /** Tighter sizing when shown in the app header */
    variant?: 'default' | 'header';
}

export function WorkerHostingInfo({
    capabilityName,
    capabilityType,
    appKey,
    variant = 'default',
}: WorkerHostingInfoProps) {
    const [workers, setWorkers] = useState<HostingWorker[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const colors = capabilityColors.worker;

    useEffect(() => {
        if (capabilityType === 'worker') {
            setLoading(false);
            return;
        }

        const endpoint = appKey
            ? `/applications/${appKey}/capabilities`
            : '/capabilities';

        setLoading(true);
        api.get(endpoint, { params: { type: 'worker' } })
            .then((res) => {
                const payload = res.data;
                const allWorkers = (
                    Array.isArray(payload)
                        ? payload
                        : Array.isArray(payload?.data)
                          ? payload.data
                          : []
                ) as HostingWorker[];
                const hosting = allWorkers.filter((w) =>
                    w.config?.capabilities_hosted?.some((c) => c.name === capabilityName),
                );
                setWorkers(hosting);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [capabilityName, capabilityType, appKey]);

    if (capabilityType === 'worker') return null;
    if (loading) return null;
    if (error) return null;

    const isHeader = variant === 'header';

    if (workers.length === 0) {
        return (
            <div
                className={
                    isHeader
                        ? `flex ${HEADER_HOSTING_HEIGHT_CLASSES} max-w-full items-center gap-[0.4125rem] overflow-x-auto overflow-y-auto rounded border border-amber-200 bg-amber-50 px-[0.66rem] py-0.5`
                        : 'flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-[1.2rem] py-2'
                }
            >
                <AlertTriangle
                    size={isHeader ? 15 : 18}
                    className="text-amber-600 shrink-0"
                />
                <div
                    className={
                        isHeader
                            ? 'min-w-0 flex-1 flex flex-col gap-[0.15rem]'
                            : 'min-w-0 flex-1'
                    }
                >
                    <p
                        className={
                            isHeader
                                ? 'whitespace-nowrap text-[12px] font-medium leading-none text-amber-800'
                                : 'text-sm font-medium text-amber-800'
                        }
                    >
                        No worker registered for this capability
                    </p>
                    <p
                        className={
                            isHeader
                                ? 'whitespace-nowrap text-[11px] leading-none text-amber-600'
                                : 'text-xs text-amber-600 mt-0'
                        }
                    >
                        Invocations will fail until a worker hosts this capability.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            className={
                isHeader
                    ? `flex ${HEADER_HOSTING_HEIGHT_CLASSES} max-w-full w-max flex-col rounded border border-slate-200 bg-white border-l-2 py-0.5 pl-[0.66rem] pr-[0.495rem] ${colors.border}`
                    : `rounded-lg border border-slate-200 bg-white py-4 px-[1.2rem] border-l-4 ${colors.border}`
            }
        >
            <div
                className={`flex shrink-0 items-center ${isHeader ? 'gap-[0.275rem] mb-[0.15rem] pb-[0.15rem]' : 'gap-1 mb-3'}`}
            >
                <Server size={isHeader ? 13 : 16} className={colors.text} />
                <h3
                    className={
                        isHeader
                            ? 'text-[12px] font-semibold leading-none text-slate-700'
                            : 'text-sm font-semibold text-slate-700'
                    }
                >
                    Hosted by {workers.length} worker{workers.length !== 1 ? 's' : ''}
                </h3>
            </div>
            <div
                className={
                    isHeader
                        ? 'min-h-0 flex-1 space-y-[0.15rem] overflow-y-auto overflow-x-hidden pr-[0.33rem]'
                        : 'space-y-2'
                }
            >
                {workers.map((w) => {
                    const dot = statusDotColors[w.status] ?? statusDotColors.offline;
                    const badge = statusBgColors[w.status] ?? statusBgColors.offline;
                    const detailPath = w.app_key
                        ? `/applications/${w.app_key}/capabilities/${w.name}`
                        : null;

                    const content = (
                        <div
                            className={
                                isHeader
                                    ? 'flex min-w-0 items-center justify-between gap-[0.4125rem] rounded-sm bg-slate-50 px-16 py-px hover:bg-slate-100 transition-colors'
                                    : 'flex items-center justify-between rounded-md bg-slate-50 px-16 py-2 hover:bg-slate-100 transition-colors'
                            }
                        >
                            <div
                                className={
                                    isHeader
                                        ? 'flex min-w-0 flex-1 items-center gap-[0.275rem] overflow-x-auto whitespace-nowrap'
                                        : 'flex min-w-0 items-center gap-2'
                                }
                            >
                                <Server size={isHeader ? 12 : 14} className="shrink-0 text-slate-500" />
                                <span
                                    className={
                                        isHeader
                                            ? 'shrink-0 font-mono text-[11px] leading-none text-slate-700'
                                            : 'font-mono text-sm text-slate-700'
                                    }
                                >
                                    {w.name}
                                </span>
                                {w.ext?.host && (
                                    <span
                                        className={
                                            isHeader
                                                ? 'shrink-0 text-[10px] leading-none text-slate-400'
                                                : 'text-xs text-slate-400'
                                        }
                                    >
                                        ({w.ext.host}{w.ext.pid ? `:${w.ext.pid}` : ''})
                                    </span>
                                )}
                            </div>
                            <span
                                className={
                                    isHeader
                                        ? `inline-flex shrink-0 items-center gap-0.5 rounded-full px-[0.3rem] py-px text-[9px] font-medium leading-none ${badge}`
                                        : `inline-flex shrink-0 items-center gap-1 rounded-full px-[0.6rem] py-0.5 text-[10px] font-medium ${badge}`
                                }
                            >
                                <Circle size={isHeader ? 7 : 6} className={`fill-current ${dot}`} />
                                {w.status}
                            </span>
                        </div>
                    );

                    return detailPath ? (
                        <Link key={w.name} to={detailPath} className="block">
                            {content}
                        </Link>
                    ) : (
                        <div key={w.name}>{content}</div>
                    );
                })}
            </div>
        </div>
    );
}
